/**
 * Central State Store & Reducer Pipeline
 * Manages contiguous ArrayBuffer state vectors and bitmask event listeners.
 */

export enum StateBitmask {
  SCENE_ID_MASK       = 0x000F, // Bits 0-3: Scene Index (0..9)
  AUDIO_ENABLED       = 0x0010, // Bit 4: Web Audio Active
  TOUCH_IGNITED       = 0x0020, // Bit 5: Scene 01 Flint Ignited
  MONOLITH_UNLOCKED   = 0x0040, // Bit 6: Scene 02 Pass Opened
  CHAOS_ALIGNED       = 0x0080, // Bit 7: Scene 03 Archway Locked
  BRIDGE_FORMED       = 0x0100, // Bit 8: Scene 04 Vault Collapsed
  FORGE_REFINED       = 0x0200, // Bit 9: Scene 05 Anvil Strikes Complete
  OCEAN_PARTED        = 0x0400, // Bit 10: Scene 06 Sea Ring Engaged
  CLOCKWORK_DISCOVERED= 0x0800, // Bit 11: Scene 07 Star Maps Revealed
  RING_DISSOLVED      = 0x1000, // Bit 12: Scene 08 Complexity Reduced
  CYBER_IMPLODED      = 0x2000, // Bit 13: Scene 09 0dB Silence Triggered
  EMBER_REBORN        = 0x4000  // Bit 14: Scene 10 Terminal Active
}

export type StateListener = (flags: number, sceneId: number, energy: number) => void;

export class StateStore {
  private static instance: StateStore;

  // Packed State Vector Buffer: [flags (Uint32), currentScene (Uint32), frictionEnergy (Float32), sparkCount (Float32)]
  private stateBuffer: ArrayBuffer;
  private flagsView: Uint32Array;
  private sceneView: Uint32Array;
  private floatView: Float32Array;

  private listeners: Set<StateListener>;

  private constructor() {
    this.stateBuffer = new ArrayBuffer(32);
    this.flagsView = new Uint32Array(this.stateBuffer, 0, 1);
    this.sceneView = new Uint32Array(this.stateBuffer, 4, 1);
    this.floatView = new Float32Array(this.stateBuffer, 8, 4);

    this.flagsView[0] = StateBitmask.AUDIO_ENABLED;
    this.sceneView[0] = 0; // Scene 01 (Index 0)
    this.floatView[0] = 0.0; // Energy accumulator
    this.listeners = new Set();
  }

  public static getInstance(): StateStore {
    if (!StateStore.instance) {
      StateStore.instance = new StateStore();
    }
    return StateStore.instance;
  }

  public subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  public getFlags(): number {
    return this.flagsView[0];
  }

  public getSceneId(): number {
    return this.sceneView[0];
  }

  public getEnergy(): number {
    return this.floatView[0];
  }

  public setSceneId(sceneId: number): void {
    if (this.sceneView[0] !== sceneId) {
      this.sceneView[0] = sceneId;
      this.notify();
    }
  }

  public setFlag(flag: StateBitmask, value: boolean): void {
    const prev = this.flagsView[0];
    if (value) {
      this.flagsView[0] |= flag;
    } else {
      this.flagsView[0] &= ~flag;
    }
    if (prev !== this.flagsView[0]) {
      this.notify();
    }
  }

  public hasFlag(flag: StateBitmask): boolean {
    return (this.flagsView[0] & flag) === flag;
  }

  public addEnergy(amount: number): void {
    this.floatView[0] += amount;
    this.notify();
  }

  public resetEnergy(): void {
    this.floatView[0] = 0;
    this.notify();
  }

  private notify(): void {
    const flags = this.flagsView[0];
    const sceneId = this.sceneView[0];
    const energy = this.floatView[0];
    this.listeners.forEach(fn => fn(flags, sceneId, energy));
  }
}
